import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, ExternalLink, GraduationCap, Calendar, Users, UsersRound, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import bookLogo from '@/assets/book-logo.png';
import { ConnectedCollective } from '@/components/ConnectedCollective';
import { AccountabilityHub } from '@/components/accountability/AccountabilityHub';
import { PageHeader } from '@/components/PageHeader';
import { User } from '@supabase/supabase-js';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { TherapistConnection } from '@/components/account/TherapistConnection';

interface LearningResource {
  id: string;
  title: string;
  description: string;
  url: string;
  icon_type: string;
  sort_order: number;
}

const ConnectAndGrow = () => {
  const navigate = useNavigate();
  const [resources, setResources] = useState<LearningResource[]>([]);
  const [user, setUser] = useState<User | null>(null);
  
  useNavigationHistory(); // Track navigation

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    loadResources();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const loadResources = async () => {
    const { data, error } = await supabase
      .from('learning_resources')
      .select('*')
      .eq('is_published', true)
      .order('sort_order', { ascending: true });

    if (!error && data) {
      setResources(data as any);
    }
  };

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'book': return <BookOpen className="h-4 w-4" />;
      case 'school': return <GraduationCap className="h-4 w-4" />;
      case 'events': return <Calendar className="h-4 w-4" />;
      default: return <ExternalLink className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-therapeutic">
      <PageHeader title="Connect & Grow" />

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
          {/* Resource Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Accountability & Challenges Card */}
            <Card className="p-6 shadow-therapy border-primary/20 bg-gradient-to-br from-secondary/5 to-accent/10 hover:shadow-gentle transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center">
                  <UsersRound className="h-5 w-5 text-background" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Accountability</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                Connect with partners, join challenges, and achieve 3x better results through mutual accountability and support.
              </p>
              <Button 
                className="w-full"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('accountability')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                Find Partners
              </Button>
            </Card>

            {/* Connected Collective Card */}
            <Card className="p-6 shadow-therapy border-primary/20 bg-gradient-to-br from-secondary/5 to-accent/10 hover:shadow-gentle transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center">
                  <Users className="h-5 w-5 text-background" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Connected Collective</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                Join our supportive community where you can share experiences, ask questions, and connect with others on their wellness journey. Support and be supported.
              </p>
              <Button 
                className="w-full"
                onClick={async (e) => {
                  e.preventDefault();
                  const { data: { user } } = await supabase.auth.getUser();
                  if (!user) {
                    navigate('/auth');
                  } else {
                    document.getElementById('community')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Join the Community
              </Button>
            </Card>

            {/* Learning Resources Card */}
            <Card className="p-6 shadow-therapy border-primary/20 bg-gradient-to-br from-accent/5 to-soft-accent/10 hover:shadow-gentle transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <img src={bookLogo} alt="Book" className="w-10 h-10 object-contain" />
                <h3 className="text-xl font-semibold text-foreground">Learning Resources</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                Explore books, courses, retreats, and workshops with The Psychic Psychologist<sup className="text-[0.6em]">®</sup> to deepen your understanding and accelerate your growth.
              </p>
              <div className="space-y-2">
                {resources.length > 0 ? (
                  resources.map((resource) => (
                    <Button key={resource.id} variant="outline" size="sm" className="w-full justify-between" asChild>
                      <a href={resource.url} target="_blank" rel="noopener noreferrer">
                        <span className="flex items-center gap-2">
                          {getIcon(resource.icon_type)}
                          {resource.title}
                        </span>
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </Button>
                  ))
                ) : (
                  <>
                    <Button variant="outline" size="sm" className="w-full justify-between" asChild>
                      <a href="https://your-book-link.com" target="_blank" rel="noopener noreferrer">
                        <span className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4" /> 
                          The Book (Paperback, Audio, Digital)
                        </span>
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-between" asChild>
                      <a href="https://your-school-link.com" target="_blank" rel="noopener noreferrer">
                        <span className="flex items-center gap-2">
                          <GraduationCap className="h-4 w-4" /> 
                          School of QuantumSelf Psychology
                        </span>
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-between" asChild>
                      <a href="https://your-events-link.com" target="_blank" rel="noopener noreferrer">
                        <span className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" /> 
                          Live Events, Retreats & Workshops
                        </span>
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </Button>
                  </>
                )}
              </div>
            </Card>
          </div>

          {/* Therapist Connection Section */}
          <div id="therapist">
            <TherapistConnection userId={user?.id || ''} />
          </div>

          {/* Accountability Section */}
          <div id="accountability">
            <AccountabilityHub />
          </div>

          {/* Connected Collective Section */}
          <div id="community">
            <ConnectedCollective />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConnectAndGrow;